import { useMemo, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import {
  useVocab,
  useCreateVocab,
  type VocabKind,
} from '../../api/queries/vocab';

interface VocabTagInputProps {
  kind: VocabKind;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  /** When true, only one value allowed (single-tag mode). */
  single?: boolean;
}

/**
 * Pick-or-create vocabulary tag input. Typing a new label and pressing Enter posts to
 * /api/v2/vocab/{kind} and instantly adds it to the selection. New entries are tagged
 * `source='user_created'` by the backend.
 */
export function VocabTagInput({
  kind,
  label,
  value,
  onChange,
  single = false,
}: VocabTagInputProps) {
  const { data: options = [] } = useVocab(kind);
  const create = useCreateVocab(kind);
  const [inputValue, setInputValue] = useState('');

  const labels = useMemo(() => options.map((o) => o.label), [options]);

  return (
    <Autocomplete
      multiple={!single}
      freeSolo
      value={single ? (value[0] ?? '') : value}
      onChange={async (_, newValue) => {
        const raw = Array.isArray(newValue)
          ? newValue
          : newValue
            ? [newValue]
            : [];
        const cleaned = raw
          .map((v) => (typeof v === 'string' ? v : '').trim())
          .filter(Boolean);
        const unknown = cleaned.filter(
          (v) => !labels.some((l) => l.toLowerCase() === v.toLowerCase()),
        );
        for (const label of unknown) {
          try {
            await create.mutateAsync(label);
          } catch {
            // ignore — caller still sees the value in the selection; next render refetch picks it up
          }
        }
        onChange(cleaned);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={labels}
      renderTags={(values: readonly string[], getTagProps) =>
        values.map((val, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const isCustom = !labels.some(
            (l) => l.toLowerCase() === val.toLowerCase(),
          );
          return (
            <Chip
              key={key}
              variant={isCustom ? 'outlined' : 'filled'}
              size="small"
              label={val}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="type or pick…"
          size="small"
        />
      )}
    />
  );
}
